import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';
import { Subject, finalize, takeUntil } from 'rxjs';

import {
  LiveKitRoomComponent,
  type LiveKitTranscriptEvent,
} from 'src/app/shared/components/livekit-room/livekit-room.component';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  AdminSessionApiService,
  PdSession,
  PdSessionConversationEntry,
  VideoRoomResult,
} from 'src/app/core/services/admin-session-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { trustMapEmbedUrl } from 'src/app/shared/utils/dom-sanitizer.util';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { KycCaptureComparisonComponent } from '../kyc-capture-comparison/kyc-capture-comparison.component';
import {
  capturedAddressError,
  enteredAddressDisplay,
  extractEnteredAddresses,
  formatCapturedAddress,
  getSessionAiResponse,
} from '../audit-trail-address.util';
import { formatSessionRuntime } from 'src/app/shared/utils/session-runtime';
import {
  isoDateDisplay,
  isoTimeDisplay,
  sessionAgentName,
  sessionEventLabel,
  sessionGeoDisplay,
  sessionHadInterview,
  sessionScoreDisplay,
  sessionStatusClass,
  sessionStatusLabel,
  sessionType,
} from '../audit-trail-display.util';
import { ReverseGeocodeService } from 'src/app/core/services/reverse-geocode.service';
import { isSuperAdminSimulatePdSession } from 'src/app/shared/utils/pd-session-list.util';

/** One workflow step that ran on a session, as surfaced by the audit-trail payload. */
export interface ReportSection {
  node_id: string | null;
  node_code: string;
  label: string;
  status: string;
  sequence: number;
  documents?: { key: string; label: string }[];
}

/** One tab in the session-detail tab strip. */
export interface DetailTab {
  id: string;
  label: string;
}

/**
 * Full-page session detail. Was a modal inside the audit trail; the content
 * (four tabs, KYC comparison, map, recording playback) outgrew a dialog and a
 * route makes a session linkable.
 */
@Component({
  selector: 'app-session-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    LiveKitRoomComponent,
    PageSurfaceHeaderComponent,
    KycCaptureComparisonComponent,
  ],
  templateUrl: './session-detail.component.html',
})
export class SessionDetailComponent implements OnInit, OnDestroy {
  private readonly api = inject(AdminSessionApiService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly reverseGeocode = inject(ReverseGeocodeService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  detailSession: PdSession | null = null;
  detailLoading = false;
  error = '';
  activeDetailTab = 'kyc';

  /** Where the Back control returns to — the list URL that opened this page. */
  backUrl = '/audit-trail';

  /** Shown when a session has no usable workflow definition — see detailTabsFrom. */
  static readonly LEGACY_TABS: DetailTab[] = [
    { id: 'kyc', label: 'KYC Details' },
    { id: 'overview', label: 'Overview' },
    { id: 'transcript', label: 'Transcript' },
    { id: 'scoring', label: 'Scoring' },
  ];

  detailTabs: DetailTab[] = SessionDetailComponent.LEGACY_TABS;
  /** The workflow steps that ran for the open session; [] when it has no workflow record. */
  sections: ReportSection[] = [];
  readonly sessionHadInterview = sessionHadInterview;

  /** The four fixed panel ids, so a legacy tab id can be recognised as its own panel. */
  private static readonly LEGACY_PANEL_IDS: ReadonlySet<string> = new Set(
    SessionDetailComponent.LEGACY_TABS.map((t) => t.id),
  );

  /** Maps a workflow node code onto the legacy panel that renders it. */
  private static readonly PANEL_BY_NODE: Record<string, string> = {
    OTP: 'kyc',
    MOBILE_OTP: 'kyc',
    DOCUMENT_CAPTURE: 'kyc',
    DOCUMENT_UPLOAD: 'kyc',
    OPERATOR_UPLOAD: 'kyc',
    BIOMETRIC: 'kyc',
    DOCUMENT_OCR: 'kyc',
    INTERVIEW: 'transcript',
    SCORING: 'scoring',
  };

  /**
   * One tab per workflow step that ran, named as the operator named it. Sessions predating the
   * flow pin, and sessions whose mirrored definition has been archived, have no sections — those
   * keep the four fixed tabs rather than rendering an empty page.
   */
  detailTabsFrom(sections: ReportSection[] | undefined): DetailTab[] {
    if (!sections?.length) return SessionDetailComponent.LEGACY_TABS;
    return sections.map((s) => ({ id: s.node_code, label: s.label }));
  }

  /**
   * Maps a tab id (a legacy id or a workflow node code) onto the legacy panel it renders. A
   * legacy id is its own panel; a node code maps through PANEL_BY_NODE; anything else has no
   * dedicated panel and shows the general overview.
   */
  panelFor(tabId: string): string {
    if (SessionDetailComponent.LEGACY_PANEL_IDS.has(tabId)) return tabId;
    return SessionDetailComponent.PANEL_BY_NODE[tabId] ?? 'overview';
  }

  /** True when the given legacy panel is the one the active tab shows — exactly one is ever true. */
  showsPanel(panel: string): boolean {
    return this.panelFor(this.activeDetailTab) === panel;
  }

  // ── Video room ─────────────────────────────────────────────────────────────
  videoRoom: VideoRoomResult | null = null;
  videoLoading = false;
  videoError = '';
  showVideoEmbed = false;
  livekitActive = false;
  private liveTranscriptEntries: PdSessionConversationEntry[] = [];

  @ViewChild(LiveKitRoomComponent) private readonly livekitRoom?: LiveKitRoomComponent;

  recordingSaving = false;
  recordingSaveError = '';
  recordingPlaybackUrl: string | null = null;
  recordingLoading = false;
  private videoFinalizeInFlight = false;

  kycGeoAddress: string | null = null;
  kycGeoAddressLoading = false;
  private kycGeoAddressRequestKey = '';

  /** Digilocker / UIDAI photo — inline on simulate sessions, fetched from tenant S3 otherwise. */
  kycAadhaarEkycImage: string | null = null;

  // Shared with the list table and CSV export.
  readonly sessionType = sessionType;
  readonly agentName = sessionAgentName;
  readonly eventLabel = sessionEventLabel;
  readonly statusLabel = sessionStatusLabel;
  readonly statusClass = sessionStatusClass;
  readonly scoreDisplay = sessionScoreDisplay;
  readonly geoDisplay = sessionGeoDisplay;

  ngOnInit(): void {
    const back = (this.route.snapshot.queryParamMap.get('back') ?? '').trim();
    if (back.startsWith('/')) {
      this.backUrl = back;
    }
    this.loadSession();
  }

  ngOnDestroy(): void {
    void this.finalizeVideoSession(true);
    this.revokeRecordingPlaybackUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSession(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(id) || id <= 0) {
      this.error = 'Invalid session id.';
      return;
    }
    // Session ids collide across tenant and control-plane databases, so the tenant
    // must travel with the id or this resolves an unrelated session.
    const tenantRaw = Number(this.route.snapshot.queryParamMap.get('tenant'));
    const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;

    this.detailLoading = true;
    this.api
      .getSessionById(id, tenantId)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.detailLoading = false)), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          if (!env.data) {
            this.error = 'Session not found.';
            return;
          }
          const session = env.data;
          if (!isSuperAdminSimulatePdSession(session)) {
            void this.router.navigate([this.backUrl], { replaceUrl: true });
            return;
          }
          this.detailSession = session;
          this.sections = session.sections ?? [];
          // The Transcript tab only means something for a session that had an interview.
          const hadInterview = sessionHadInterview(session, this.sections);
          this.detailTabs = this.detailTabsFrom(session.sections).filter(
            (tab) => this.panelFor(tab.id) !== 'transcript' || hadInterview,
          );
          this.activeDetailTab = this.detailTabs[0]?.id ?? 'kyc';
          this.resolveAadhaarEkycImage(session, tenantId);
          this.loadKycGeoAddress(session);
          this.loadRecordingPlaybackIfSaved(session);
        },
        error: () => {
          this.error = 'Could not load session details.';
        },
      });
  }

  goBack(): void {
    void this.router.navigateByUrl(this.backUrl);
  }

  /** Refresh the session after a captured-vs-uploaded "Compare Again". */
  onKycSessionUpdated(session: PdSession): void {
    if (session && this.detailSession?.id === session.id) {
      this.detailSession = session;
      const inline = session.ai_response?.face?.uploaded_image;
      if (inline) this.kycAadhaarEkycImage = inline;
    }
  }

  showKycTab(): void {
    // On a workflow-labelled session the KYC panel lives under a node-code tab; pick that tab so
    // the strip highlights it. The literal is the fallback for the legacy four.
    const tab = this.detailTabs.find((t) => this.panelFor(t.id) === 'kyc');
    this.activeDetailTab = tab?.id ?? 'kyc';
  }

  // ── Display helpers ────────────────────────────────────────────────────────

  dateDisplay(s: PdSession): string {
    return isoDateDisplay(s.created_at);
  }

  timeDisplay(s: PdSession): string {
    return isoTimeDisplay(s.created_at);
  }

  runtimeDisplay(s: PdSession): string {
    return formatSessionRuntime(s);
  }

  conversationTimeDisplay(iso: string | null | undefined): string {
    if (!iso?.trim()) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${isoDateDisplay(iso)} ${isoTimeDisplay(iso)}`;
  }

  transcriptLog(s: PdSession): PdSessionConversationEntry[] {
    let raw = s.conversation_log ?? [];
    // Defensive: backend Json column occasionally arrives as a serialised string — parse it back.
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch { raw = []; }
    }
    if (!Array.isArray(raw)) raw = [];
    const stored: PdSessionConversationEntry[] = raw;
    if (!this.liveTranscriptEntries.length) return stored;
    return [...stored, ...this.liveTranscriptEntries];
  }

  // ── Video room ─────────────────────────────────────────────────────────────

  get livekitServerUrl(): string {
    return (this.videoRoom?.livekit_url ?? this.videoRoom?.room_url ?? '').trim();
  }

  get livekitHostToken(): string {
    return (this.videoRoom?.host_token ?? '').trim();
  }

  hasSavedRecording(s: PdSession | null): boolean {
    return Boolean(s?.metadata?.has_video_recording);
  }

  startVideoRoom(): void {
    if (!this.detailSession) return;
    this.videoLoading = true;
    this.videoError = '';
    this.api
      .createVideoRoom(this.detailSession.id)
      .pipe(finalize(() => (this.videoLoading = false)), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          if (env?.status && env.data) {
            this.videoRoom = env.data;
            if (env.data.is_configured && (env.data.livekit_url || env.data.room_url) && env.data.host_token) {
              this.videoFinalizeInFlight = false;
              this.showVideoEmbed = true;
              this.livekitActive = true;
              this.liveTranscriptEntries = [];
            } else {
              this.videoError =
                'LiveKit is not configured on the server. Add LIVEKIT_URL, LIVEKIT_API_KEY, and LIVEKIT_API_SECRET to backend .env.';
            }
          } else {
            this.videoError = env?.message ?? 'Could not create video room.';
          }
        },
        error: () => { this.videoError = 'Failed to create video room.'; },
      });
  }

  closeVideoEmbed(): void {
    void this.finalizeVideoSession(false);
  }

  onLivekitSessionEnded(): void {
    void this.finalizeVideoSession(false);
  }

  private async finalizeVideoSession(leavingPage: boolean): Promise<void> {
    if (!this.detailSession || this.videoFinalizeInFlight) return;
    this.videoFinalizeInFlight = true;

    let recordingBlob: Blob | null = null;
    if (this.livekitRoom) {
      recordingBlob = await this.livekitRoom.stopSessionRecording();
    }
    this.showVideoEmbed = false;
    this.livekitActive = false;

    const log = this.transcriptLog(this.detailSession);
    const shouldSave = Boolean(recordingBlob?.size) || this.liveTranscriptEntries.length > 0;

    if (!shouldSave) {
      this.videoFinalizeInFlight = false;
      return;
    }

    this.recordingSaving = true;
    this.recordingSaveError = '';
    this.api
      .saveSessionRecording(this.detailSession.id, {
        recording: recordingBlob ?? undefined,
        conversation_log: log.length ? log : undefined,
      })
      // Deliberately NOT bound to destroy$: this fires from ngOnDestroy when the
      // user navigates away, and cancelling it would discard the recording. As a
      // modal, the component stayed alive and the save completed.
      .pipe(
        finalize(() => {
          this.recordingSaving = false;
          this.videoFinalizeInFlight = false;
        }),
      )
      .subscribe({
        next: (env) => {
          if (env?.status && env.data) {
            this.detailSession = env.data;
            if (!leavingPage) {
              this.revokeRecordingPlaybackUrl();
              this.loadRecordingPlaybackIfSaved(env.data);
            }
          } else {
            this.recordingSaveError = env?.message ?? 'Could not save recording.';
          }
        },
        error: () => {
          this.recordingSaveError = 'Failed to save video recording. Try again.';
        },
      });
  }

  private loadRecordingPlaybackIfSaved(s: PdSession): void {
    if (!this.hasSavedRecording(s)) return;
    this.recordingLoading = true;
    this.api
      .getSessionRecordingBlob(s.id)
      .pipe(finalize(() => (this.recordingLoading = false)), takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.revokeRecordingPlaybackUrl();
          if (blob.size > 0) {
            this.recordingPlaybackUrl = URL.createObjectURL(blob);
          }
        },
        error: () => {
          this.recordingSaveError = 'Saved recording could not be loaded.';
        },
      });
  }

  private revokeRecordingPlaybackUrl(): void {
    if (this.recordingPlaybackUrl) {
      URL.revokeObjectURL(this.recordingPlaybackUrl);
      this.recordingPlaybackUrl = null;
    }
  }

  onAuditLivekitTranscript(ev: LiveKitTranscriptEvent): void {
    if (!ev.final) return;
    const role = ev.role === 'ai' ? 'ai' : 'customer';
    const text = ev.text.trim();
    if (!text) return;
    const last = this.liveTranscriptEntries.at(-1);
    if (last?.role === role && last.text === text) return;
    this.liveTranscriptEntries.push({ role, text, time: ev.time });
  }

  // ── KYC tab ────────────────────────────────────────────────────────────────

  get kycModalSelectedRecord(): string {
    const s = this.detailSession;
    if (!s) return '—';
    const name = s.ai_response?.pan?.captured_name ?? s.customer_name ?? null;
    const docNum = s.ai_response?.pan?.captured_number ?? null;
    if (name && docNum) return `${name} (${docNum})`;
    if (name) return name;
    if (docNum) return docNum;
    return s.customer_name ?? '—';
  }

  get kycModalFaceMatch(): string {
    const face = this.detailSession?.ai_response?.face;
    if (!face || face.error) return '—';
    return `${Math.round(face.score)}%`;
  }

  get kycModalFaceMatchClass(): string {
    const face = this.detailSession?.ai_response?.face;
    if (!face || face.error) return 'font-medium text-gray-900 dark:text-white';
    return face.matched
      ? 'font-medium text-emerald-700 dark:text-emerald-400'
      : 'font-medium text-red-600 dark:text-red-400';
  }

  get kycModalLiveness(): string {
    const face = this.detailSession?.ai_response?.face;
    if (!face) return 'Not assessed';
    if (face.liveness_error) return 'Error';
    const score = face.liveness_score ?? 0;
    return face.liveness_verified ? `Live — ${score}/100` : `Not live — ${score}/100`;
  }

  get kycModalLivenessClass(): string {
    const face = this.detailSession?.ai_response?.face;
    if (!face || face.liveness_error) return 'font-medium text-gray-400 dark:text-gray-500';
    return face.liveness_verified
      ? 'inline-flex w-fit items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
      : 'inline-flex w-fit items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-950/60 dark:text-red-300';
  }

  get kycAadhaarOtpApproved(): boolean {
    const s = this.detailSession;
    if (!s) return false;
    if (s.metadata?.['aadhaar_otp_verified'] === true) return true;
    return Boolean(s.ai_response?.pan && !s.ai_response.pan.error);
  }

  get kycModalPanOcrHasResult(): boolean {
    const pan = this.detailSession?.ai_response?.pan;
    return pan != null && !pan.error;
  }

  get kycModalPanOcrMatched(): boolean {
    return this.detailSession?.ai_response?.pan?.matched ?? false;
  }

  get kycModalDob(): string {
    return this.formatKycDob(this.detailSession?.ai_response?.pan?.date_of_birth);
  }

  get kycEnteredAddress(): string | null {
    return enteredAddressDisplay(extractEnteredAddresses(this.detailSession));
  }

  get kycCapturedAddress(): string | null {
    return formatCapturedAddress(this.detailSession);
  }

  get kycCapturedAddressError(): string | null {
    return capturedAddressError(this.detailSession);
  }

  get kycAadhaarBackImage(): string | null {
    return getSessionAiResponse(this.detailSession)?.aadhaar_back?.captured_image ?? null;
  }

  get kycModalLatLng(): string {
    const m = this.detailSession?.metadata;
    if (m?.geo_lat != null && m?.geo_lng != null) {
      return `${Number(m.geo_lat).toFixed(4)}, ${Number(m.geo_lng).toFixed(4)}`;
    }
    return 'Not captured';
  }

  private resetKycGeoAddress(): void {
    this.kycGeoAddressRequestKey = '';
    this.kycGeoAddress = null;
    this.kycGeoAddressLoading = false;
  }

  private loadKycGeoAddress(session: PdSession | null): void {
    const m = session?.metadata;
    if (m?.geo_lat == null || m?.geo_lng == null) {
      this.resetKycGeoAddress();
      return;
    }

    const lat = Number(m.geo_lat);
    const lng = Number(m.geo_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      this.resetKycGeoAddress();
      return;
    }

    const requestKey = `${lat},${lng}`;
    if (requestKey === this.kycGeoAddressRequestKey) return;

    this.kycGeoAddressRequestKey = requestKey;
    this.kycGeoAddress = null;
    this.kycGeoAddressLoading = true;

    this.reverseGeocode
      .lookup(lat, lng)
      .pipe(finalize(() => (this.kycGeoAddressLoading = false)), takeUntil(this.destroy$))
      .subscribe((address) => {
        if (this.kycGeoAddressRequestKey === requestKey) {
          this.kycGeoAddress = address;
        }
      });
  }

  get kycPanCardImage(): string | null {
    return this.detailSession?.ai_response?.pan?.captured_image ?? null;
  }

  get kycSelfieImage(): string | null {
    return this.detailSession?.ai_response?.face?.captured_image ?? null;
  }

  /** Prefer the photo written onto the session; otherwise load the tenant e-KYC object. */
  private resolveAadhaarEkycImage(session: PdSession, tenantId?: number): void {
    const inline = session.ai_response?.face?.uploaded_image ?? null;
    if (inline) {
      this.kycAadhaarEkycImage = inline;
      return;
    }
    this.kycAadhaarEkycImage = null;
    this.api
      .getKycReferenceImage(session.id, 'face', tenantId ?? session.tenant_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.data?.source === 'aadhaar_ekyc' && res.data.image) {
            this.kycAadhaarEkycImage = res.data.image;
          }
        },
      });
  }

  get kycMapUrl(): SafeResourceUrl | null {
    const m = this.detailSession?.metadata;
    if (m?.geo_lat == null || m?.geo_lng == null) return null;
    const lat = Number(m.geo_lat).toFixed(6);
    const lng = Number(m.geo_lng).toFixed(6);
    const delta = 0.01;
    const bbox = `${(Number(m.geo_lng) - delta).toFixed(6)},${(Number(m.geo_lat) - delta).toFixed(6)},${(Number(m.geo_lng) + delta).toFixed(6)},${(Number(m.geo_lat) + delta).toFixed(6)}`;
    const url = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
    return trustMapEmbedUrl(this.sanitizer, url);
  }

  private formatKycDob(dob: string | null | undefined): string {
    if (!dob?.trim()) return '—';
    const parsed = new Date(dob);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return dob;
  }
}
